import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Divider,
    Typography,
    Box,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Autocomplete, Chip } from '@mui/material';
import api from '../services/api';
import { ICliente, IProduto } from '../interfaces';

interface TenantEditProps {
    open: boolean;
    onClose: () => void;
    formCliente: ICliente;
    setFormCliente: React.Dispatch<React.SetStateAction<ICliente>>;
    updateTenantList: () => void; // Função para atualizar a lista de tenants
    handleEnderecoChange: (field: keyof ICliente['endereco'], value: string) => void;
    handleContatoChange: (field: keyof ICliente['contato'], value: any) => void;
    handleContatoTelefoneChange: (field: keyof ICliente['contato']['telefone'], value: any) => void;
}

const TenantEdit: React.FC<TenantEditProps> = ({
    open,
    onClose,
    formCliente,
    setFormCliente,
    updateTenantList,
    handleEnderecoChange,
    handleContatoChange,
    handleContatoTelefoneChange
}) => {
    const [loading, setLoading] = useState(false);
    const [allProducts, setAllProducts] = useState<IProduto[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<IProduto[]>([]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const resp = await api.get('/api/products');
                setAllProducts(resp.data || []);
            } catch (err) {
                console.error('Erro ao carregar produtos:', err);
            }
        };
        fetchProducts();
    }, []);

    useEffect(() => {
        if (open) {
            const selected = allProducts.filter(p => (formCliente.products || []).includes(p._id));
            setSelectedProducts(selected);
        }
    }, [open, allProducts, formCliente.products]);

    const handleSave = async () => {
        setLoading(true);
        try {
            await api.put(`/api/tenants/${formCliente.tenantId}`, formCliente);
            if (updateTenantList) {
                updateTenantList(); // Atualiza a lista de tenants, se a função foi passada
            }
            onClose(); // Fecha o diálogo após salvar
        } catch (error) {
            console.error('Erro ao atualizar cliente:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Editar Cliente</DialogTitle>
            <DialogContent>
                <TextField
                    label="Razão Social"
                    value={formCliente.razaoSocial}
                    onChange={e => setFormCliente({ ...formCliente, razaoSocial: e.target.value })}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Nome Fantasia"
                    value={formCliente.nomeFantasia}
                    onChange={e => setFormCliente({ ...formCliente, nomeFantasia: e.target.value })}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="CNPJ"
                    value={formCliente.CNPJ}
                    onChange={e => setFormCliente({ ...formCliente, CNPJ: e.target.value })}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="E-mail"
                    value={formCliente.email}
                    onChange={e => setFormCliente({ ...formCliente, email: e.target.value })}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Telefone"
                    value={formCliente.telefone}
                    onChange={e => setFormCliente({ ...formCliente, telefone: e.target.value })}
                    fullWidth
                    margin="normal"
                />
                <Autocomplete
                    multiple
                    options={allProducts}
                    getOptionLabel={(option) => option.name}
                    value={selectedProducts}
                    onChange={(_, value) => {
                        setSelectedProducts(value);
                        const ids = value.map(v => v._id);
                        setFormCliente({ ...formCliente, products: ids });
                    }}
                    renderTags={(value: IProduto[], getTagProps) =>
                        value.map((option: IProduto, index: number) => (
                            <Chip label={option.name} {...getTagProps({ index })} key={option._id} />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField {...params} label="Produtos" placeholder="Selecione produtos" margin="normal" />
                    )}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={formCliente.ativo}
                            onChange={e => setFormCliente({ ...formCliente, ativo: e.target.checked })}
                        />
                    }
                    label="Ativo"
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6">Endereço</Typography>
                <Box sx={{ mb: 2, border: '1px solid #eee', p: 2, borderRadius: 2 }}>
                    <TextField
                        label="CEP"
                        value={formCliente.endereco.CEP}
                        onChange={e => handleEnderecoChange('CEP', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="Logradouro"
                        value={formCliente.endereco.logradouro}
                        onChange={e => handleEnderecoChange('logradouro', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="Número"
                        value={formCliente.endereco.numero}
                        onChange={e => handleEnderecoChange('numero', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="Complemento"
                        value={formCliente.endereco.complemento}
                        onChange={e => handleEnderecoChange('complemento', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="Bairro"
                        value={formCliente.endereco.bairro}
                        onChange={e => handleEnderecoChange('bairro', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="Município"
                        value={formCliente.endereco.municipio}
                        onChange={e => handleEnderecoChange('municipio', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="UF"
                        value={formCliente.endereco.UF}
                        onChange={e => handleEnderecoChange('UF', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6">Contato</Typography>
                <Box sx={{ mb: 2, border: '1px solid #eee', p: 2, borderRadius: 2 }}>
                    <TextField
                        label="Nome"
                        value={formCliente.contato.nome}
                        onChange={e => handleContatoChange('nome', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="CPF"
                        value={formCliente.contato.cpf}
                        onChange={e => handleContatoChange('cpf', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="Estado Civil"
                        value={formCliente.contato.estadoCivil}
                        onChange={e => handleContatoChange('estadoCivil', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="DDD"
                        value={formCliente.contato.telefone.ddd}
                        onChange={e => handleContatoTelefoneChange('ddd', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <TextField
                        label="Número"
                        value={formCliente.contato.telefone.numero}
                        onChange={e => handleContatoTelefoneChange('numero', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formCliente.contato.telefone.whatsapp}
                                onChange={e => handleContatoTelefoneChange('whatsapp', e.target.checked)}
                            />
                        }
                        label="WhatsApp"
                    />
                    <TextField
                        label="Cargo/Função"
                        value={formCliente.contato.cargoFuncao}
                        onChange={e => handleContatoChange('cargoFuncao', e.target.value)}
                        margin="dense"
                        fullWidth
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formCliente.contato.responsavelTecnico}
                                onChange={e => handleContatoChange('responsavelTecnico', e.target.checked)}
                            />
                        }
                        label="Responsável Técnico"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formCliente.contato.responsavelFinanceiro}
                                onChange={e => handleContatoChange('responsavelFinanceiro', e.target.checked)}
                            />
                        }
                        label="Responsável Financeiro"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TenantEdit;